import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShip, faAnchor, faLifeRing, faCompass, faBrain, faChartLine } from '@fortawesome/free-solid-svg-icons';
import { Line, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { gapsData, layersData, collaborationModels, chartData } from './dummyData';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

const MaritimeHome = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-[#0f172a] to-[#1e3a8a] text-white">
      {/* Hero – Compact & Mobile-First */}
      <header className="py-2 px-3 text-center bg-gradient-to-b from-black to-[#0f172a]">
        <div className="max-w-4xl mx-auto">
          <FontAwesomeIcon icon={faShip} className="text-3xl 2 text-[#3b82f6] lg:text-6xl lg:mb-8" />
          <h1 className="text-2xl font-bold mb-4 lg:text-5xl">⚓ Think5Whys Maritime</h1>
          <p className="text-sm leading-relaxed max-w-3xl mx-auto opacity-90 lg:text-lg ">
            The decision-intelligence and human-thinking layer that maritime and deep-tech companies are missing between technology, people, and outcomes.
          </p>
          <FontAwesomeIcon icon={faCompass} className="text-4xl mt-8 text-[#3b82f6] lg:text-5xl lg:mt-10" />
        </div>
      </header>

      {/* Dashboard Metrics – Smaller Cards & Responsive Charts */}
      <section className="py-2 px-3 mx-auto">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          <div className="bg-[#1e293b] rounded-xl p-6 shadow-lg">
            <h3 className="text-sm lg:text-lg font-semibold mb-4 flex items-center gap-3">
              <FontAwesomeIcon icon={faChartLine} className="text-lg" /> Decision Quality Trend
            </h3>
            <div className="h-56 md:h-72">
              <Line 
                data={chartData.decisionQuality} 
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { labels: { color: 'white', font: { size: 12 } } } },
                  scales: { x: { ticks: { color: 'white', font: { size: 11 } } }, y: { ticks: { color: 'white', font: { size: 11 } } } }
                }} 
              />
            </div>
          </div>
          <div className="bg-[#1e293b] rounded-xl p-6 shadow-lg">
            <h3 className="text-sm lg:text-lg font-semibold mb-4 flex items-center gap-3">
              <FontAwesomeIcon icon={faLifeRing} className="text-sm lg:text-lg" /> Root Causes Institutionalized
            </h3>
            <div className="h-56 md:h-72">
              <Bar 
                data={chartData.incidentsResolved} 
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { labels: { color: 'white', font: { size: 12 } } } },
                  scales: { x: { ticks: { color: 'white', font: { size: 11 } } }, y: { ticks: { color: 'white', font: { size: 11 } } } }
                }} 
              />
            </div>
          </div>
        </div>
      </section>

      {/* Core Gaps & Think5Whys Fit – Sleek Compact Cards */}
      <section className="py-2 px-3 mx-auto">
        <h2 className="text-lg lg:text-xl font-bold text-center mb-2 flex items-center justify-center gap-3">
          <FontAwesomeIcon icon={faBrain} className="text-lg lg:text-xl " /> Core Industry Gaps & Think5Whys Fit
        </h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 place-items-center">
          {gapsData.map(gap => (
            <div key={gap.id} className="bg-[#1e293b] rounded-lg p-6 shadow-lg hover:shadow-xl transition">
              <h3 className="text-lg lg:text-xl font-semibold mb-3 text-[#3b82f6]">{gap.title}</h3>
              <ul className="mb-2 space-y-1.5 text-sm">
                {gap.symptoms.map((s, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <FontAwesomeIcon icon={faAnchor} className="text-xs mt-1 flex-shrink-0" /> 
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
              <p className="text-gray-300 text-sm leading-relaxed">{gap.fit}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Layered Collaboration Model – Tighter Spacing */}
      <section className="py-2 px-3 bg-[#0f172a]">
        <h2 className="text-lg font-bold text-center mb-2 lg:text-xl">Where Think5Whys Fits — Layered Model</h2>
        <div className="max-w-5xl mx-auto grid grid-cols-1 gap-8 lg:grid-cols-3 place-items-center">
          {layersData.map(layer => (
            <div key={layer.layer} className="bg-gradient-to-b from-[#1e3a8a] to-[#3b82f6] rounded-xl p-6 text-center shadow-lg">
              <div className="text-xl font-bold mb-3 lg:text-2xl">Layer {layer.layer}</div>
              <h3 className="text-lg font-bold mb-3 lg:text-xl">{layer.title}</h3>
              <ul className="mb-4 space-y-1.5 text-sm">
                {layer.items?.map((item, i) => (
                  <li key={i}>• {item}</li>
                ))}
              </ul>
              {layer.methods && <p className="italic mb-3 text-sm">Using: {layer.methods.join(", ")}</p>}
              <p className="font-semibold text-base">Outcome: {layer.outcome}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Collaboration Models – Compact */}
      <section className="py-2 px-3 mx-auto">
        <h2 className="text-xl lg:text-2xl font-bold text-center mb-2">Collaboration Models</h2>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 place-items-center">
          {collaborationModels.map(cm => (
            <div key={cm.model} className="bg-[#1e293b] rounded-lg p-6 shadow-lg border border-[#3b82f6]/50">
              <h3 className="text-lg font-bold mb-3 lg:text-xl ">Model {cm.model}</h3>
              <h4 className="text-lg font-semibold mb-3 lg:text-xl ">{cm.title}</h4>
              <p className="text-gray-300 text-sm leading-relaxed">{cm.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Credibility – Clean Table */}
      <section className="py-2 px-3 bg-[#0f172a] text-center flex flex-col justify-center">
        <h2 className="text-lg font-bold mb-2 lg:text-xl">Why We Are Credible</h2>
        <div className="max-w-3xl mx-auto overflow-x-auto">
          <table className="w-full text-left border-collapse text-sm lg:text-base">
            <thead>
              <tr className="border-b border-[#3b82f6]/50">
                <th className="py-3 text-left">Dimension</th>
                <th className="py-3 text-left">Why It Matters</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-700"><td className="py-3">MCA</td><td>Systems architecture</td></tr>
              <tr className="border-b border-gray-700"><td className="py-3">Maths (Hons)</td><td>Modeling, abstraction</td></tr>
              <tr className="border-b border-gray-700"><td className="py-3">MBA Finance</td><td>Capital & ROI thinking</td></tr>
              <tr className="border-b border-gray-700"><td className="py-3">Six Sigma Black Belt</td><td>Process + RCA</td></tr>
              <tr><td className="py-3">Founder-Architect</td><td>Vision + execution</td></tr>
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-lg italic lg:text-xl">We are not a vendor. We are a Decision Architect.</p>
      </section>

      <footer className="py-4 text-center bg-black text-sm">
        <p>© 2025 Think5Whys Maritime Module • Embedded Cognition for the Seas</p>
      </footer>
    </div>
  );
};

export default MaritimeHome;